import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useVocabularyInfo } from '@/components/vocabulary/useVocabularyInfo';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

import { HpoTree } from './HpoTree';
import { SearchBox } from './SearchBox';
import { SearchResultsList } from './SearchResultsList';

/** Root of the HPO "is-a" hierarchy — universal HPO constant, not project data. */
const HPO_ROOT_ID = 'HP:0000118';
const SEARCH_DEBOUNCE_MS = 200;
/** Search-mode kicks in only after 2 characters so typing doesn't flicker
 *  the body between tree and results on every keystroke. */
const SEARCH_MIN_CHARS = 2;

interface HpoBrowseDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly excludedHpoIds: ReadonlySet<string>;
  readonly onAddTerm: (term: { id: string; label: string }) => void;
}

/**
 * Visual HPO term browser (PTV2-1122). Data-driven from the
 * vocabulary-service — no static seed: the top level is the children of
 * HP:0000118 ("Phenotypic abnormality") and each node lazily fetches its
 * own `/terms/{id}/children` when first expanded. Click a row to attach
 * the term to the patient; the dialog stays open so several can be added
 * in one pass, and rows already on the record render greyed with a check.
 *
 * Typing in the search box switches the body to a flat results list backed
 * by the existing `/vocabularies/hpo/search` endpoint, since matching
 * arbitrary terms back into the tree would require a per-path lookup the
 * service doesn't expose.
 *
 * Body / chrome / tree / list are each their own sibling file under this
 * folder; this index just owns the dialog shell and the search-vs-tree
 * mode flip.
 */
export function HpoBrowseDialog({
  open,
  onOpenChange,
  excludedHpoIds,
  onAddTerm,
}: HpoBrowseDialogProps): ReactElement {
  const { t } = useTranslation();
  const info = useVocabularyInfo('hpo');
  const display = info?.display ?? null;
  const [query, setQuery] = useState('');
  const trimmed = query.trim();
  const debounced = useDebouncedValue(trimmed, SEARCH_DEBOUNCE_MS);
  const searchActive = debounced.length >= SEARCH_MIN_CHARS;
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // Reset the query whenever the dialog closes so reopening starts clean,
  // and focus the input on every open so the user can type immediately.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);
  useEffect(() => {
    if (open) searchInputRef.current?.focus();
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('patient.phenotypes.browse.title')}</DialogTitle>
          <DialogDescription>
            {searchActive
              ? t('patient.phenotypes.browse.searchDescription')
              : t('patient.phenotypes.browse.description')}
          </DialogDescription>
        </DialogHeader>
        <SearchBox value={query} onChange={setQuery} inputRef={searchInputRef} />
        {searchActive ? (
          <SearchResultsList
            query={debounced}
            display={display}
            excludedHpoIds={excludedHpoIds}
            onAddTerm={onAddTerm}
          />
        ) : (
          <div className="max-h-[60vh] overflow-y-auto rounded-md border border-border bg-card">
            <HpoTree
              rootId={HPO_ROOT_ID}
              display={display}
              excludedHpoIds={excludedHpoIds}
              onAddTerm={onAddTerm}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
